import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  MessageSquare, ThumbsUp, ChevronDown, ChevronUp, Send, Trash2, Shield, Loader2, MessageCircle,
} from 'lucide-react';

const CourseDiscussions = ({ courseId }) => {
  const { api, user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sort, setSort] = useState('newest');
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState(new Set());
  const [replies, setReplies] = useState({});
  const [replyInputs, setReplyInputs] = useState({});

  const fetchPosts = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const res = await api.get(`/discussions/course/${courseId}?page=${page}&sort=${sort}`);
      setPosts(res.data.posts || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [api, courseId, page, sort]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handlePost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);
    try {
      await api.post(`/discussions/course/${courseId}`, { content: newPost });
      setNewPost('');
      fetchPosts();
      toast.success('Posted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post');
    }
    finally { setPosting(false); }
  };

  const handleReply = async (postId) => {
    const content = replyInputs[postId]?.trim();
    if (!content) return;
    try {
      await api.post(`/discussions/course/${courseId}`, { content, parent_id: postId });
      setReplyInputs(p => ({ ...p, [postId]: '' }));
      loadReplies(postId);
      fetchPosts();
      toast.success('Reply posted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reply');
    }
  };

  const handleVote = async (postId, currentVoted) => {
    try {
      const res = await api.post(`/discussions/post/${postId}/vote`, {
        direction: currentVoted ? 'none' : 'up'
      });
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, upvotes: res.data.upvotes, user_voted: res.data.user_voted } : p
      ));
    } catch { toast.error('Vote failed'); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await api.delete(`/discussions/post/${postId}`);
      fetchPosts();
      toast.success('Post removed');
    } catch { toast.error('Delete failed'); }
  };

  const loadReplies = async (postId) => {
    try {
      const res = await api.get(`/discussions/post/${postId}/replies`);
      setReplies(prev => ({ ...prev, [postId]: res.data }));
    } catch { /* silent */ }
  };

  const toggleReplies = (postId) => {
    const next = new Set(expandedReplies);
    if (next.has(postId)) {
      next.delete(postId);
    } else {
      next.add(postId);
      if (!replies[postId]) loadReplies(postId);
    }
    setExpandedReplies(next);
  };

  const isAdmin = user?.role === 'super_admin' || user?.role === 'content_manager';

  return (
    <div className="mt-8 border-t border-slate-200 pt-8" data-testid="course-discussions">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-[#2a3e5d] flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-[#20acf6]" />
          Discussion ({total})
        </h2>
        <div className="flex gap-2">
          {['newest', 'popular'].map(s => (
            <button
              key={s}
              onClick={() => { setSort(s); setPage(1); }}
              data-testid={`sort-${s}`}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                sort === s ? 'bg-[#20acf6]/10 text-[#20acf6]' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {s === 'newest' ? 'Newest' : 'Popular'}
            </button>
          ))}
        </div>
      </div>

      {/* New Post */}
      {user ? (
        <div className="mb-6 bg-slate-50 rounded-xl p-4" data-testid="new-post-area">
          <textarea
            value={newPost}
            onChange={(e) => setNewPost(e.target.value)}
            placeholder="Share a question, insight, or tip..."
            rows={3}
            className="w-full bg-white border border-slate-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#20acf6]/30 focus:border-[#20acf6] placeholder:text-slate-400"
            data-testid="new-post-input"
          />
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-slate-400">{newPost.length}/2000</p>
            <Button
              size="sm"
              className="bg-[#20acf6] hover:bg-[#3bb8f7]"
              onClick={handlePost}
              disabled={posting || !newPost.trim()}
              data-testid="submit-post-btn"
            >
              {posting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Post
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400 mb-6 text-center py-4 bg-slate-50 rounded-xl">
          Sign in and enroll in this course to join the discussion.
        </p>
      )}

      {/* Posts */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#20acf6]" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <MessageCircle className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">No discussions yet</p>
          <p className="text-xs mt-1">Be the first to start a conversation!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map(post => (
            <div key={post.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors" data-testid={`post-${post.id}`}>
              {/* Post Header */}
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 bg-[#20acf6]/10 rounded-full flex items-center justify-center text-[#20acf6] text-[10px] font-bold">
                  {post.author_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <span className="text-sm font-medium text-slate-800">{post.author_name}</span>
                {(post.author_role === 'super_admin' || post.author_role === 'content_manager') && (
                  <Badge className="bg-purple-100 text-purple-700 text-[9px] gap-0.5"><Shield className="h-2.5 w-2.5" /> Staff</Badge>
                )}
                <span className="text-xs text-slate-400 ml-auto">
                  {post.created_at ? new Date(post.created_at).toLocaleDateString() : ''}
                </span>
              </div>

              {/* Content */}
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{post.content}</p>

              {/* Actions */}
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                <button
                  onClick={() => handleVote(post.id, post.user_voted)}
                  className={`flex items-center gap-1 text-xs font-medium transition-colors ${
                    post.user_voted ? 'text-[#20acf6]' : 'text-slate-400 hover:text-slate-600'
                  }`}
                  data-testid={`vote-${post.id}`}
                >
                  <ThumbsUp className={`h-3.5 w-3.5 ${post.user_voted ? 'fill-[#20acf6]' : ''}`} />
                  {post.upvotes || 0}
                </button>

                <button
                  onClick={() => toggleReplies(post.id)}
                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 font-medium"
                  data-testid={`toggle-replies-${post.id}`}
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                  {post.reply_count || 0} {post.reply_count === 1 ? 'reply' : 'replies'}
                  {expandedReplies.has(post.id) ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>

                {(isAdmin || post.author_id === user?.id) && (
                  <button
                    onClick={() => handleDelete(post.id)}
                    data-testid={`delete-post-${post.id}`}
                    className="text-xs text-slate-400 hover:text-red-500 ml-auto"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Replies */}
              {expandedReplies.has(post.id) && (
                <div className="mt-3 pl-4 border-l-2 border-slate-100 space-y-3">
                  {(replies[post.id] || []).map(reply => (
                    <div key={reply.id} className="py-2" data-testid={`reply-${reply.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-slate-700">{reply.author_name}</span>
                        <span className="text-[10px] text-slate-400">{reply.created_at ? new Date(reply.created_at).toLocaleDateString() : ''}</span>
                        {(isAdmin || reply.author_id === user?.id) && (
                          <button onClick={() => handleDelete(reply.id)} data-testid={`delete-reply-${reply.id}`} className="text-slate-300 hover:text-red-400 ml-auto">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-slate-600">{reply.content}</p>
                    </div>
                  ))}
                  {/* Reply input */}
                  {user && (
                    <div className="flex gap-2 pt-2">
                      <input
                        value={replyInputs[post.id] || ''}
                        onChange={(e) => setReplyInputs(p => ({ ...p, [post.id]: e.target.value }))}
                        placeholder="Write a reply..."
                        data-testid={`reply-input-${post.id}`}
                        className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#20acf6]"
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(post.id); } }}
                      />
                      <Button size="sm" variant="ghost" onClick={() => handleReply(post.id)} className="text-[#20acf6]" data-testid={`reply-submit-${post.id}`}>
                        <Send className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm text-slate-500 flex items-center px-3">{page} / {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
};

export default CourseDiscussions;
